/**
 * Generated by orval v6.23.0 🍺
 * Do not edit manually.
 * APIs for RCI FE
 * This document describes the APIs provided by NeoAP BE for RCI FE, designed to be integrated into NeoAP FE using `iframe`.\
These APIs utilize cookie-based authentication. All API requests must include the cookie `_ap_session`.

 * OpenAPI spec version: 1
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import type { BadRequestResponse } from '../model/badRequestResponse';
import type { ForbiddenResponse } from '../model/forbiddenResponse';
import type { GenerateExportFileUrl200 } from '../model/generateExportFileUrl200';
import type { NotFoundResponse } from '../model/notFoundResponse';
import type { SearchExportFilesRequest } from '../model/searchExportFilesRequest';
import type { SearchExportFilesResponse } from '../model/searchExportFilesResponse';
import type { UnauthorizedResponse } from '../model/unauthorizedResponse';
import type { UnprocessableEntityResponse } from '../model/unprocessableEntityResponse';
import { useCustomClient } from '../../mutator/custom-instance';
import type { ErrorType, BodyType } from '../../mutator/custom-instance';
import { customFomDataFn } from '../../api/formData/custom-function';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P
) => any
  ? P
  : never;

/**
 * @summary Export files based on the provided parameters
 */
export const useSearchExportFilesHook = () => {
  const searchExportFiles = useCustomClient<SearchExportFilesResponse>();

  return (
    searchExportFilesRequest: BodyType<SearchExportFilesRequest>,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return searchExportFiles(
      {
        url: `/api/js/export_files/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: searchExportFilesRequest,
      },
      options
    );
  };
};

export const getSearchExportFilesQueryKey = (
  searchExportFilesRequest: SearchExportFilesRequest
) => {
  return [`/api/js/export_files/search`, searchExportFilesRequest] as const;
};

export const useSearchExportFilesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  searchExportFilesRequest: SearchExportFilesRequest,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSearchExportFilesQueryKey(searchExportFilesRequest);

  const searchExportFiles = useSearchExportFilesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>
  > = () => searchExportFiles(searchExportFilesRequest, requestOptions);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type SearchExportFilesQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>
>;
export type SearchExportFilesQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Export files based on the provided parameters
 */
export const useSearchExportFiles = <
  TData = Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  searchExportFilesRequest: SearchExportFilesRequest,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useSearchExportFilesQueryOptions(
    searchExportFilesRequest,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useSearchExportFilesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  searchExportFilesRequest: SearchExportFilesRequest,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getSearchExportFilesQueryKey(searchExportFilesRequest);

  const searchExportFiles = useSearchExportFilesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>
  > = () => searchExportFiles(searchExportFilesRequest, requestOptions);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type SearchExportFilesSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>
>;
export type SearchExportFilesSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Export files based on the provided parameters
 */
export const useSearchExportFilesSuspense = <
  TData = Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  searchExportFilesRequest: SearchExportFilesRequest,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchExportFilesHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useSearchExportFilesSuspenseQueryOptions(
    searchExportFilesRequest,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Generate presigned download URL for export file
 */
export const useGenerateExportFileUrlHook = () => {
  const generateExportFileUrl = useCustomClient<GenerateExportFileUrl200>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return generateExportFileUrl(
      { url: `/api/js/export_files/${id}/download`, method: 'GET', signal },
      options
    );
  };
};

export const getGenerateExportFileUrlQueryKey = (id: string) => {
  return [`/api/js/export_files/${id}/download`] as const;
};

export const useGenerateExportFileUrlQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGenerateExportFileUrlQueryKey(id);

  const generateExportFileUrl = useGenerateExportFileUrlHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>
  > = ({ signal }) => generateExportFileUrl(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GenerateExportFileUrlQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>
>;
export type GenerateExportFileUrlQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Generate presigned download URL for export file
 */
export const useGenerateExportFileUrl = <
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGenerateExportFileUrlQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGenerateExportFileUrlSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGenerateExportFileUrlQueryKey(id);

  const generateExportFileUrl = useGenerateExportFileUrlHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>
  > = ({ signal }) => generateExportFileUrl(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GenerateExportFileUrlSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>
>;
export type GenerateExportFileUrlSuspenseQueryError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Generate presigned download URL for export file
 */
export const useGenerateExportFileUrlSuspense = <
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateExportFileUrlHook>>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = useGenerateExportFileUrlSuspenseQueryOptions(
    id,
    options
  );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Delete an Export File
 */
export const useDeleteExportFileHook = () => {
  const deleteExportFile = useCustomClient<void>();

  return (
    id: string,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return deleteExportFile(
      { url: `/api/js/export_files/${id}`, method: 'DELETE' },
      options
    );
  };
};

export const useDeleteExportFileMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteExportFileHook>>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteExportFileHook>>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const deleteExportFile = useDeleteExportFileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteExportFileHook>>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteExportFile(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteExportFileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteExportFileHook>>>
>;

export type DeleteExportFileMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Delete an Export File
 */
export const useDeleteExportFile = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteExportFileHook>>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions = useDeleteExportFileMutationOptions(options);

  return useMutation(mutationOptions);
};
